import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { units, Grid, Typography, Card, Link } from '@hbf/dsl';
import { useSelector } from 'react-redux';

import {
  ListingCostPayableByValues,
  ListingCost,
  ListingCostKey,
  ListingPricingType,
} from '@ha/api/v2/types/Listing';

import {
  TRANSLATIONS_COST_LABEL_BY_KEY,
  TRANSLATIONS_COST_OPTIONS_BY_ATTRIBUTE,
} from 'ha/modules/ListingEdit/components/StructuredCostsFieldGroup/translations';

import { CurrencyFormat } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { CostsUtilityKeys } from 'ha/models/ListingCosts/types';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { normalizeDate } from '@ha/date';
import { PricingStructure } from 'ha/modules/Pricing';
import { ContractTypeOptions } from 'ha/constants/ContractTypes';
import { TextTooltip } from 'ha/components.legacy/TextTooltip';
import { InstalmentsList } from './InstalmentsList';
import { PaymentOverviewButton } from '../../components/PaymentOverviewButton';
import { openDesiredDatesPicker } from '../../helpers/openDesiredDatesPicker';
import { TRANSLATIONS_COST_LDP_BY_ATTRIBUTE } from '../../translations';
import { useLocalPricing } from '../../hooks/useLocalPricing';
import { useDesiredDates } from '../../hooks/useDesiredDates';
import {
  getCosts,
  getCurrency,
  getDesiredDates,
  getMinimalRentalPeriod,
  getPrice,
  getListingEntity,
  getRentalConditions,
  getMaximumStay,
  getIsBelongsToPartner,
} from '../../selectors';

import {
  getRequiredDepositsTotal,
  getDuringYourStayCosts,
  getOptionalCostsEnumerated,
} from '../../helpers/getCosts';

import { DesiredDatesPickerTrigger } from '../../containers/DesiredDatesPicker';
import { IncludedCheckmark } from './components/IncludedCheckmark';
import { CostRow } from './components/CostRow';
import { getInstalmentsPricing } from './getInstalmentsPricing';

const useStyles = makeStyles()(theme => ({
  card: {
    padding: units.rem(1),
    '&:not(:last-child)': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderBottomStyle: 'none',
    },
    '&:not(:first-child)': {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    '&.paymentDialogWrapper': {
      marginTop: units.rem(-1),
      paddingTop: 0,
      borderTopStyle: 'none',
    },
  },
  title: {
    fontSize: units.rem(1),
    textTransform: 'uppercase',
    color: theme.palette.grey[600],
  },
  paymentOverviewButton: {
    paddingTop: theme.spacing(1.3),
  },
  description: {
    color: theme.palette.grey[500],
    paddingBottom: theme.spacing(1),
  },
  subtitle: {
    marginTop: theme.spacing(1),
  },
}));

const estimatedKeys: ListingCostKey[] = Object.values(CostsUtilityKeys);

interface Props {
  isCharitable?: boolean;
  isFreeListing?: boolean;
}

/* eslint-disable complexity */
const ListingRentalConditionsSection: React.FC<Props> = ({
  isCharitable = false,
  isFreeListing = false,
}) => {
  const { T, formatDate, formatCurrency } = useIntl();
  const { classes } = useStyles();
  const { lg } = useMediaQuery();

  const costs = useSelector(getCosts);
  const currency = useSelector(getCurrency);
  const desiredDates = useSelector(getDesiredDates);
  const minimalPeriod = useSelector(getMinimalRentalPeriod);
  const listing = useSelector(getListingEntity);
  const price = useSelector(getPrice);
  const rentalConditions = useSelector(getRentalConditions);
  const maximumStay = useSelector(getMaximumStay);
  const isBelongsToPartner = useSelector(getIsBelongsToPartner);

  const { pricingStructure } = useLocalPricing();

  const { hasDesiredDates } = useDesiredDates();

  /**
   * Costs for the "During Your Stay" section
   */
  const duringYourStayCosts = React.useMemo(
    () => Object.entries(getDuringYourStayCosts(costs)),
    [costs],
  ) as [ListingCostKey, ListingCost][];

  /**
   * Costs for the "Optional Services" section
   */
  const optionalServicesCosts = React.useMemo(
    () => Object.entries(getOptionalCostsEnumerated(costs)),
    [costs],
  ) as [ListingCostKey, ListingCost][];

  const currencyFormat =
    currency === 'EUR'
      ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
      : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;

  const formatCostInfo = React.useCallback(
    (costKey: ListingCostKey, cost: ListingCost, isOptional = false) => {
      if (costKey !== 'utility-bills') {
        if (cost.payableBy === ListingCostPayableByValues.INCLUDED_IN_RENT) {
          return <IncludedCheckmark />;
        }

        if (cost.payableBy === ListingCostPayableByValues.NOT_APPLICABLE) {
          return T('StructuredCosts.create.nonapplicable');
        }

        if (cost.payableBy === ListingCostPayableByValues.UNKNOWN) {
          return T(TRANSLATIONS_COST_OPTIONS_BY_ATTRIBUTE.payableBy.unknown);
        }
      }

      let priceInfo = formatCurrency(cost.value, currency, currencyFormat);

      if (costKey !== 'utility-bills') {
        if (cost.payableAt) {
          priceInfo += ` ${T(
            TRANSLATIONS_COST_LDP_BY_ATTRIBUTE.payableAt[cost.payableAt],
          )}`;
        }

        if (
          cost.isEstimated &&
          !isOptional &&
          estimatedKeys.includes(costKey)
        ) {
          priceInfo += ` ${T('StructuredCosts.Estimated')}`;
        }
      }

      return priceInfo;
    },
    [T, currency, currencyFormat, formatCurrency],
  );

  const formatMaximumStay = React.useCallback(() => {
    if (maximumStay < 1) {
      const weeks = Math.floor(maximumStay / 0.25);
      return weeks === 1 ? `${weeks} ${T('week')}` : T('%d weeks', weeks);
    }

    return `${maximumStay} ${maximumStay === 1 ? T('month') : T('months')}`;
  }, [T, maximumStay]);

  const requiredDepositsTotal = React.useMemo(
    () => getRequiredDepositsTotal(costs),
    [costs],
  );
  const instalmentsPricing = getInstalmentsPricing(
    rentalConditions,
    listing.pricingType,
    formatDate,
    formatCurrency,
  );
  const { isCrossedPrice, instalmentsPrices } = instalmentsPricing;
  return (
    <React.Fragment>
      <Grid container data-test-locator="ListingRentalConditionsSection">
        <Grid item xs={12} md={10} lg={8}>
          {!(isCharitable && isFreeListing) &&
            !isBelongsToPartner &&
            pricingStructure === PricingStructure.instalment && (
              <Card className={classes.card} variant="outlined" elevation={0}>
                <Grid container direction="column">
                  <Grid item xs>
                    <Typography.H4 className={classes.title}>
                      {T('Rental period')}
                    </Typography.H4>
                  </Grid>
                  <Grid item xs="auto" container direction="row">
                    <Grid item xs>
                      {lg ? (
                        <Link
                          to=""
                          kind="mint-underline"
                          onClick={openDesiredDatesPicker}
                        >
                          {hasDesiredDates
                            ? `${formatDate(
                                'D MMM YYYY',
                                desiredDates.startDate!,
                              )} - ${formatDate(
                                'D MMM YYYY',
                                desiredDates.endDate!,
                              )}`
                            : T('LDP.rentalConditions.addDates')}
                        </Link>
                      ) : (
                        <DesiredDatesPickerTrigger>
                          {({ onClick }) => (
                            <Link
                              to=""
                              data-test-locator="ListingRentalConditionsSection/DesiredDatesPickerTrigger"
                              kind="mint-underline"
                              onClick={onClick}
                            >
                              {hasDesiredDates
                                ? `${formatDate(
                                    'D MMM YYYY',
                                    desiredDates.startDate!,
                                  )} - ${formatDate(
                                    'D MMM YYYY',
                                    desiredDates.endDate!,
                                  )}`
                                : T('LDP.rentalConditions.addDates')}
                            </Link>
                          )}
                        </DesiredDatesPickerTrigger>
                      )}
                    </Grid>
                    {rentalConditions &&
                      (rentalConditions.instalments.length ?? 0) >= 0 && (
                        <Grid item xs="auto">
                          {T(
                            '%d instalments',
                            (rentalConditions?.instalments.length ?? 0) + 1,
                          )}
                        </Grid>
                      )}
                  </Grid>
                </Grid>
              </Card>
            )}
          {minimalPeriod > 0 && (
            <Card
              className={classes.card}
              variant="outlined"
              elevation={0}
              data-test-locator="ListingRentalConditionsSection/MinimumStay"
            >
              <Grid container>
                <Grid item xs={6}>
                  <Typography.H4 className={classes.title}>
                    {T('Minimum stay')}
                  </Typography.H4>
                </Grid>
                <Grid container item xs={6} justifyContent="flex-end">
                  {`${minimalPeriod} ${
                    minimalPeriod === 1 ? T('month') : T('months')
                  }`}
                </Grid>
              </Grid>
            </Card>
          )}
          {isCharitable && maximumStay > 0 && (
            <Card
              className={classes.card}
              variant="outlined"
              elevation={0}
              data-test-locator="ListingRentalConditionsSection/MaximumStay"
            >
              <Grid container>
                <Grid item xs={6}>
                  <Typography.H4 className={classes.title}>
                    {T('ukr_response.search.maximum_stay')}
                  </Typography.H4>
                </Grid>
                <Grid container item xs={6} justifyContent="flex-end">
                  {formatMaximumStay()}
                </Grid>
              </Grid>
            </Card>
          )}
          {!(isCharitable && isFreeListing) && (
            <Card className={classes.card} variant="outlined" elevation={0}>
              <Grid container>
                <Grid item xs={6}>
                  <Typography.H4 className={classes.title}>
                    {T('Deposits')}
                  </Typography.H4>
                </Grid>
                <Grid container item xs={6} justifyContent="flex-end">
                  {formatCurrency(
                    requiredDepositsTotal,
                    currency,
                    currencyFormat,
                  )}
                </Grid>
              </Grid>
            </Card>
          )}
          <Card className={classes.card} variant="outlined" elevation={0}>
            <Grid container>
              <Grid item xs={12}>
                <Typography.H4 className={classes.title}>
                  {T('StructuredCosts.LDP.duringyourstay')}
                </Typography.H4>
              </Grid>
              {pricingStructure === PricingStructure.instalment && (
                <Grid item xs={12}>
                  <Typography.ParagraphTitle className={classes.subtitle}>
                    {T('Rent')}
                  </Typography.ParagraphTitle>
                </Grid>
              )}
              {!(isCharitable && isFreeListing) && hasDesiredDates && (
                <Grid item xs={12}>
                  <Typography.Paragraph className={classes.description}>
                    {T(
                      'listings.rent_summary.advanced_price_excess.info_1',
                      T(ContractTypeOptions[listing.contractType].title),
                    )}
                    <TextTooltip
                      tooltip={T(
                        ContractTypeOptions[listing.contractType].tooltip,
                      )}
                    />
                    {'. '}
                    {isCrossedPrice &&
                      T('listings.rent_summary.advanced_price_excess.info_2')}
                  </Typography.Paragraph>
                </Grid>
              )}
              {pricingStructure === PricingStructure.instalment &&
                !hasDesiredDates &&
                !!listing.pricingValues?.monthly &&
                listing.pricingType === ListingPricingType.MONTHLY && (
                  <CostRow
                    title={T('Monthly')}
                    testLocator="Monthly range"
                    value={`${formatCurrency(
                      Math.min(
                        ...(Object.values(
                          listing.pricingValues.monthly,
                        ) as Array<number>),
                      ),
                      currency,
                      currencyFormat,
                    )} - ${formatCurrency(
                      Math.max(
                        ...(Object.values(
                          listing.pricingValues.monthly,
                        ) as Array<number>),
                      ),
                      currency,
                      currencyFormat,
                    )}`}
                  />
                )}
              {pricingStructure === PricingStructure.instalment &&
                !hasDesiredDates &&
                listing.pricingType === ListingPricingType.FLAT && (
                  <CostRow
                    title={T('Monthly')}
                    value={`${formatCurrency(price, currency, currencyFormat)}`}
                    testLocator="ListingRentalConditionsSection/FirstMonthRent"
                  />
                )}

              {pricingStructure === PricingStructure.instalment &&
                desiredDates.startDate &&
                rentalConditions && (
                  <InstalmentsList
                    instalmentsPrices={instalmentsPrices}
                    firstInstalment={rentalConditions.firstInstalment}
                    desiredDates={desiredDates}
                  />
                )}
              {pricingStructure === PricingStructure.instalment && (
                <Grid item xs={12}>
                  <Typography.ParagraphTitle className={classes.subtitle}>
                    {T('ldp.utilities')}
                  </Typography.ParagraphTitle>
                </Grid>
              )}
              {duringYourStayCosts
                .filter(
                  ([, cost]) =>
                    // Hide costs that are not applicable
                    ![ListingCostPayableByValues.NOT_APPLICABLE].includes(
                      cost.payableBy as ListingCostPayableByValues,
                    ),
                )
                .map(([costKey, cost]) => (
                  <CostRow
                    key={costKey}
                    title={T(TRANSLATIONS_COST_LABEL_BY_KEY[costKey])}
                    value={formatCostInfo(costKey, cost!)}
                    testLocator={`ListingRentalConditionsSection/${costKey}`}
                  />
                ))}
            </Grid>
          </Card>

          {pricingStructure === PricingStructure.instalment && hasDesiredDates && (
            <Card
              className={`${classes.card} paymentDialogWrapper`}
              variant="outlined"
              elevation={0}
            >
              <div className={classes.paymentOverviewButton}>
                <PaymentOverviewButton
                  startDate={normalizeDate(desiredDates.startDate!)}
                />
              </div>
            </Card>
          )}
          {!(isCharitable && isFreeListing) &&
            optionalServicesCosts.length > 0 && (
              <Card className={classes.card} variant="outlined" elevation={0}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography.H4 className={classes.title}>
                      {T('StructuredCosts.create.optionalservices')}
                    </Typography.H4>
                  </Grid>
                  {optionalServicesCosts.map(([costKey, cost]) => (
                    <CostRow
                      key={costKey}
                      title={T(TRANSLATIONS_COST_LABEL_BY_KEY[costKey])}
                      value={formatCostInfo(costKey, cost, true)}
                      testLocator={`ListingRentalConditionsSection/${costKey}`}
                    />
                  ))}
                </Grid>
              </Card>
            )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export { ListingRentalConditionsSection };
